import {
  buildFastFoodRestaurantSchema as _buildFastFoodRestaurantSchema,
  buildMenuItemSchema as _buildMenuItemSchema,
  buildMenuSchema as _buildMenuSchema,
} from '@crocodille/schema-org';

export type {
  BranchSchemaData,
  MenuSectionData,
  ProductSchemaData,
} from '@crocodille/schema-org';

const BASE_URL = 'https://bbdomu.cz';
const BB_LOGO = `${BASE_URL}/_next/static/media/bb_logo.d23eff64.svg`;

const RESTAURANT_CONFIG = {
  baseUrl: BASE_URL,
  logo: BB_LOGO,
  servesCuisine: ['Bagety', 'Sandwiches', 'Fast Food'],
} as const;

// --- Global schemas (static) ---

export const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${BASE_URL}/#organization`,
  name: 'Bageterie Boulevard',
  legalName: 'Crocodille ČR, spol. s r. o.',
  url: BASE_URL,
  logo: BB_LOGO,
  description:
    'Bageterie Boulevard je česká síť rychlého občerstvení specializující se na čerstvé bagety, wrapy, saláty a kavárnu. Provozuje více než 70 restaurací po celé České republice s možností doručení domů nebo vyzvednutí na místě.',
  foundingDate: '2000',
  vatID: 'CZ43001343',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Podébradská 206/57',
    postalCode: '198 00',
    addressLocality: 'Praha 9',
    addressCountry: 'CZ',
  },
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+420800224389',
      contactType: 'customer service',
      contactOption: 'TollFree',
      availableLanguage: 'cs',
      areaServed: 'CZ',
    },
    {
      '@type': 'ContactPoint',
      email: 'bbdomu@bb.cz',
      contactType: 'customer support',
    },
  ],
  sameAs: [
    'https://www.facebook.com/bageterie.boulevard/',
    'https://www.instagram.com/bageterieboulevard/',
  ],
  numberOfEmployees: {
    '@type': 'QuantitativeValue',
    minValue: 500,
  },
  areaServed: {
    '@type': 'Country',
    name: 'Czech Republic',
  },
};

export const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${BASE_URL}/#website`,
  url: BASE_URL,
  name: 'BB Domů – Oblíbené bagety až k tobě | Bageterie Boulevard',
  description:
    'Objednávej čerstvé bagety, wrapy a saláty z Bageterie Boulevard. Doručení domů do 45 minut nebo vyzvednutí za 6 minut. Více než 70 restaurací po celé ČR.',
  publisher: { '@id': `${BASE_URL}/#organization` },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${BASE_URL}/menu?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
  inLanguage: 'cs',
};

// --- Schema builders ---

import type {
  BranchSchemaData,
  ProductSchemaData,
} from '@crocodille/schema-org';

export function buildFastFoodRestaurantSchema(
  branch: BranchSchemaData,
): Record<string, unknown> {
  return _buildFastFoodRestaurantSchema(branch, RESTAURANT_CONFIG);
}

export function buildMenuItemSchema(
  product: ProductSchemaData,
  taxonSlug: string,
): Record<string, unknown> {
  return _buildMenuItemSchema(
    product,
    `${BASE_URL}/menu/${taxonSlug}/${product.slug}`,
  );
}

export function buildMenuSchema(
  taxonName: string,
  taxonSlug: string,
  products: ProductSchemaData[],
): Record<string, unknown> {
  return _buildMenuSchema(
    [{ name: taxonName, products }],
    `${BASE_URL}/menu/${taxonSlug}`,
    `${taxonName} | BB Domů`,
    `${BASE_URL}/#website`,
    (p) => `${BASE_URL}/menu/${taxonSlug}/${p.slug}`,
  );
}
